import { ReactElement } from 'react';
import { Link, useRoutes } from 'react-router-dom';
import { ticketStates, type Ticket, type TicketState } from 'modules/support/types';
import { ModelScene, EntityScene, DetailsScene, IndexScene } from 'components';
import { SelectField } from '@ff-it/form';
import { never } from 'core/permissions';
import { parseISO } from 'date-fns';
import { ViewTicket } from './View';
import { TableColumns } from '@ff-it/ui';
import { TickStateTag } from 'modules/support/SupportStateTag';
import { createOptions, Option } from 'options';
import { formatCustomSince } from 'utilities';
import { UserAvatar } from 'components/UserAvatar';

const cols: TableColumns<Ticket> = [
  {
    content: 'State',
    width: 110,
    key: 'state',
    sortable: true,
    render: ({ state }: Ticket) => <TickStateTag state={state} appearance="naked" />,
  },
  {
    content: 'Ticket',
    key: 'number',
    render: ({ id, title }: Ticket) => (
      <>
        <strong className="mr-2">#{id}</strong>
        <Link to={`${id}`}>{title}</Link>
      </>
    ),
    sortable: true,
  },
  {
    content: 'Agency',
    key: 'enterprise.company.title',
  },

  {
    content: 'Created',
    key: 'created_at',
    render: ({ created_at }: Ticket) => formatCustomSince(parseISO(created_at)),
    width: 140,
  },
  {
    content: 'Author',
    key: 'author',
    cellProps: { textAlign: 'center' },
    width: 90,
    render: ({ author }: Ticket) => <UserAvatar user={author} size="xs" />,
  },
  {
    content: 'Assignee',
    key: 'assignee',
    cellProps: { textAlign: 'center' },
    width: 90,
    render: ({ assignee }: Ticket) => (assignee ? <UserAvatar user={assignee} size="xs" /> : null),
  },
];

const model = {
  endpoint: 'support/tickets/',
  title: 'Ticket',
  pluralTitle: 'Tickets',
  entityTitle: (e: Ticket) => `#${e.id}`,
  //   permissions,
};

export const ticketStateOptions: Option<TicketState>[] = createOptions(ticketStates);

export function Tickets(): ReactElement {
  const routes = useRoutes([
    {
      index: true,
      element: (
        <IndexScene
          columns={cols}
          canCreate={never}
          storageKey="dashboard_tickets"
          filterFields={
            <SelectField
              name="state"
              simple
              size="sm"
              placeholder="State"
              options={ticketStateOptions}
              isClearable={false}
            />
          }
        />
      ),
    },
    {
      path: ':id/',
      element: <EntityScene />,
      children: [
        {
          index: true,
          element: (
            <DetailsScene heading={null}>
              <ViewTicket />
            </DetailsScene>
          ),
        },
      ],
    },
  ]);
  return <ModelScene {...model}>{routes}</ModelScene>;
}
